import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment.dev';
import { MessageHandlerService } from '../services/message-handler.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { UserDataManager } from '../user/UserDataManager';
import { User } from '../models/user';
import { ERROR_MESSAGES } from '../common/Constants';
import { createEmbeddingContext, DashboardExperience } from 'amazon-quicksight-embedding-sdk';

@Component({
  selector: 'app-quick-sight-details',
  templateUrl: './quick-sight-dashboard.component.html',
  styleUrls: ['./quick-sight-dashboard.component.scss'],
})
export class QuickSightDashboardComponent implements OnInit, OnDestroy {
  currentUser: User;
  dashboardUrl: SafeResourceUrl | null = null;
  isLoading: boolean = false;
  errorMessage: string = '';
  subscriptions: Subscription[] = [];
  genderParam: string = 'Human'; // Default value
  dashboardId: string | null = null;

  private lastLoadedDashboardId: string | null = null;
  private readonly defaultDashboardId = 'default-dashboard-id';

  // Reference to the container element
  @ViewChild('dashboardContainer', { static: true })
  dashboardContainer!: ElementRef<HTMLDivElement>;

  // Dashboard embedding instance
  private dashboardExperience?: DashboardExperience;

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private messageHandlerService: MessageHandlerService,
    private progressBarService: ProgressBarService,
    private userDataManager: UserDataManager,
  ) {}

  ngOnInit(): void {
    this.dashboardId = this.defaultDashboardId;

    this.subscriptions.push(
      this.userDataManager.user$.subscribe({
        next: (user) => {
          if (user) {
            this.currentUser = user;
            this.maybeLoadDashboard();
          }
        },
        error: (error) => {
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.USER_PROFILE_FETCH,
            error.message || ERROR_MESSAGES.USER_PROFILE_FETCH,
          );
        },
      }),
    );
  }

  ngOnDestroy(): void {
    // Clean up the dashboard experience by clearing the container
    if (this.dashboardContainer) {
      this.dashboardContainer.nativeElement.innerHTML = '';
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private maybeLoadDashboard(): void {
    if (!this.currentUser) return;
    if (!this.dashboardId) {
      this.errorMessage = 'Missing dashboard id in URL';
      return;
    }

    if (this.lastLoadedDashboardId === this.dashboardId) return;
    this.lastLoadedDashboardId = this.dashboardId;
    this.loadQuickSightDashboard(this.dashboardId);
  }

  private loadQuickSightDashboard(dashboardId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.progressBarService.setProgressBarVisibility(true);

    // Clean up existing dashboard if any
    if (this.dashboardContainer) {
      this.dashboardContainer.nativeElement.innerHTML = '';
    }

    this.subscriptions.push(
      this.http
        .post<{ embedUrl: string; dashboardId: string; expiresAt: string }>(
          environment.endPoints.quicksight,
          {
            dashboardId,
          },
        )
        .subscribe({
          next: async (response) => {
            await this.embedDashboard(response.embedUrl);
            this.isLoading = false;
            this.progressBarService.setProgressBarVisibility(false);
          },
          error: (error) => {
            this.errorMessage =
              error?.error?.errorMessage ||
              error?.errorMessage ||
              error?.message ||
              'Failed to load QuickSight dashboard';
            this.messageHandlerService.generateToastMessage(
              'error',
              'Dashboard Load Failed',
              this.errorMessage,
            );
            this.isLoading = false;
            this.progressBarService.setProgressBarVisibility(false);
          },
        }),
    );
  }

  private async embedDashboard(embedUrl: string): Promise<void> {
    try {
      // Ensure the container element exists before embedding
      if (!this.dashboardContainer || !this.dashboardContainer.nativeElement) {
        throw new Error('Dashboard container element is not available');
      }

      // Create embedding context
      const embeddingContext = await createEmbeddingContext();

      // Define parameters for the dashboard
      const dashboardParameters = [
        {
          Name: 'GenderParam2',
          Values: [this.genderParam || 'Human'],
        },
      ];

      // Embed the dashboard with parameters
      this.dashboardExperience = await embeddingContext.embedDashboard(
        {
          url: embedUrl,
          container: this.dashboardContainer.nativeElement,
          width: '100%',
          height: '800px',
        },
        {
          parameters: dashboardParameters,
          // Optional: Add other content options
          onMessage: (message) => {
            console.log('Dashboard message:', message);
          },
        },
      );
    } catch (error) {
      this.errorMessage = 'Failed to embed QuickSight dashboard';
      this.messageHandlerService.generateToastMessage(
        'error',
        'Dashboard Embed Failed',
        this.errorMessage,
      );
    }
  }

  // Method to update dashboard parameters dynamically
  async updateDashboardParameters(parameters: Array<{ Name: string; Values: string[] }>): Promise<void> {
    if (this.dashboardExperience) {
      try {
        // Use the correct API to set parameters
        await this.dashboardExperience.setParameters(parameters);
      } catch (error) {
        console.error('Failed to update dashboard parameters:', error);
        this.messageHandlerService.generateToastMessage(
          'error',
          'Parameter Update Failed',
          'Failed to update dashboard parameters',
        );
      }
    }
  }

  onRetry(): void {
    // allow retry even if previous attempt failed
    this.lastLoadedDashboardId = null;
    this.maybeLoadDashboard();
  }

  onSubmitGenderParam(): void {
    // Trim whitespace from the input
    const trimmedParam = this.genderParam?.trim();

    if (!trimmedParam) {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Invalid Input',
        'Please enter a valid Gender Param value',
      );
      return;
    }

    // Update the model with trimmed value
    this.genderParam = trimmedParam;

    if (this.dashboardExperience) {
      // Dashboard is already loaded, update parameters dynamically
      const dashboardParameters = [
        {
          Name: 'GenderParam2',
          Values: [this.genderParam],
        },
      ];
      this.updateDashboardParameters(dashboardParameters);
    } else if (this.currentUser) {
      // If dashboard is not yet loaded, reload it with the new parameter
      this.lastLoadedDashboardId = null;
      this.maybeLoadDashboard();
    }
  }

}

